/**
 * SensitiveDataSessionStore — pure state logic for in-memory Category A field values.
 *
 * P13 Phase 2, session 27. Spec: docs/living/sensitive-data-export-spec.md v2.2
 *
 * CRITICAL CONSTRAINTS:
 * - Values are NEVER written to localStorage, sessionStorage, IndexedDB, cookies,
 *   or any other client-side persistent store. React state only.
 * - Values are NEVER sent to our servers. No API calls with Category A content.
 * - Tab close discards in-memory values by design. The Q4a banner (Safari/Firefox)
 *   is the prevention; lossy tab-close is the architecturally clean correction.
 *
 * This file is pure TypeScript (no JSX) so it can be imported and unit-tested
 * independently of the React provider. See sensitive-data-session-provider.tsx
 * for the React context / hook wrappers.
 */

// ---------------------------------------------------------------------------
// State types
// ---------------------------------------------------------------------------

export interface FieldRecord {
  value: string;
  /** True once the current value has been written to the .oesif file on disk. */
  saved: boolean;
}

export interface SessionState {
  fields: Record<string, FieldRecord>;
  /** True after the planner completes SecureFileOnboardingStep on this device/session. */
  setupComplete: boolean;
  /**
   * Chrome/Edge: FileSystemFileHandle for silent auto-save.
   * Safari/Firefox: null (download-on-demand path).
   * Typed as `unknown` so the pure .ts file has no browser API dependency;
   * the React provider casts it to FileSystemFileHandle | null.
   */
  fileHandle: unknown;
  /** Passphrase held in memory for the session — required for subsequent auto-saves. */
  passphrase: string | null;
}

export const INITIAL_SESSION_STATE: SessionState = {
  fields: {},
  setupComplete: false,
  fileHandle: null,
  passphrase: null,
};

// ---------------------------------------------------------------------------
// Actions and reducer
// ---------------------------------------------------------------------------

export type SessionAction =
  | { type: "SET"; fieldId: string; value: string }
  | { type: "CLEAR"; fieldId: string }
  | { type: "MARK_SAVED"; fieldIds: string[] }
  | { type: "CLEAR_ALL" }
  | { type: "COMPLETE_SETUP"; fileHandle: unknown; passphrase: string }
  | { type: "RESET_SETUP" }
  // s88 (SDE Phase 1): bulk-load values from an opened .oesif file (Phase 3). Loaded
  // values are `saved:true` — they already live in the file on disk.
  | { type: "LOAD"; values: Record<string, string> };

export function sessionReducer(
  state: SessionState,
  action: SessionAction
): SessionState {
  switch (action.type) {
    case "SET":
      return {
        ...state,
        fields: {
          ...state.fields,
          [action.fieldId]: { value: action.value, saved: false },
        },
      };
    case "CLEAR": {
      const next = { ...state.fields };
      delete next[action.fieldId];
      return { ...state, fields: next };
    }
    case "MARK_SAVED":
      return {
        ...state,
        fields: Object.fromEntries(
          Object.entries(state.fields).map(([id, rec]) => [
            id,
            action.fieldIds.includes(id) ? { ...rec, saved: true } : rec,
          ])
        ),
      };
    case "CLEAR_ALL":
      return { ...state, fields: {}, setupComplete: false, fileHandle: null, passphrase: null };
    case "COMPLETE_SETUP":
      return { ...state, setupComplete: true, fileHandle: action.fileHandle, passphrase: action.passphrase };
    case "RESET_SETUP":
      return { ...state, setupComplete: false, fileHandle: null, passphrase: null };
    case "LOAD":
      return {
        ...state,
        fields: Object.fromEntries(
          Object.entries(action.values).map(([k, v]) => [k, { value: v, saved: true }])
        ),
      };
  }
}

// ---------------------------------------------------------------------------
// Public interface (implemented in the React provider)
// ---------------------------------------------------------------------------

export interface SensitiveDataSession {
  /** Raw values keyed by a composite `${fieldId}::${rowId ?? 'card'}` key (s88 — rowId
   *  disambiguates the same template field across table/expandable rows). Never persisted
   *  outside React state. */
  values: Record<string, string>;
  /** Set or update a Category A value at the composite key. Marks it dirty (unsaved). */
  setValue: (key: string, value: string) => void;
  /** Read a single Category A value, or undefined if not in the store (s88). */
  getValue: (key: string) => string | undefined;
  /** Remove a Category A value from the session store. */
  clearValue: (key: string) => void;
  /** Called by the save flow after a successful write to the .oesif file. */
  markSaved: (keys: string[]) => void;
  /** All in-memory Category A values (for Phase 2 file serialization) (s88). */
  getAllValues: () => Record<string, string>;
  /** Bulk-load values from an opened .oesif file (Phase 3). Marks them saved (s88). */
  loadValues: (values: Record<string, string>) => void;
  /** True when the store holds any Category A value at all (s88). */
  hasValues: boolean;
  /** Count of in-memory values not yet written to the file — drives the unsaved badge (s88). */
  unsavedCount: number;
  /** Returns composite keys whose values have not yet been written to disk. */
  getDirtyFields: () => string[];
  /** True when any field has an unsaved value. Drives Q4a banner and LogoutSaveCheck. */
  hasUnsavedChanges: boolean;
  /** Discard all in-memory values. Called on logout and cleanup. */
  clearAll: () => void;
  /** True once the planner has completed SecureFileOnboardingStep on this device/session. */
  isSetupComplete: boolean;
  /** Chrome/Edge only: the FileSystemFileHandle for silent auto-save. null on Safari/Firefox. */
  fileHandle: FileSystemFileHandle | null;
  /** Passphrase held in memory for the session — used for subsequent saves without re-prompting. */
  passphrase: string | null;
  /** Called by SecureFileOnboardingStep when the planner completes setup. */
  completeSetup: (fileHandle: FileSystemFileHandle | null, passphrase: string) => void;
  /** Called by Q6 "forgot passphrase" flow to restart onboarding. */
  resetSetup: () => void;
}
